import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { validateMyGarageToken } from '@/services/api/mygarage-auth';
import {
  getOrderComments,
  createOrderComment,
  confirmOrder,
  cancelOrder,
  ArtisanOrderComment,
} from '@/services/api/artisan-order-comments';
import { ArtisanOrder } from '@/services/api/artisan-orders';
import { AISHA_API_BASE } from '@/constants';
import OrderStatusProgress from '@/components/OrderStatusProgress';

const OrderConfirmationBoardView: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === '1';
  const [order, setOrder] = useState<ArtisanOrder | null>(null);
  const [comments, setComments] = useState<ArtisanOrderComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const init = async () => {
      try {
        let uid = '';
        let uname = '';
        try {
          const user = await validateMyGarageToken();
          if (user) {
            uid = user.id;
            uname = user.name || '';
          }
        } catch (e) {
          console.error('MyGarage認証エラー:', e);
        }
        if (!uid) {
          const smsUserId = localStorage.getItem('aineko-sms-user-id');
          const smsUserName = localStorage.getItem('aineko-sms-user-name');
          if (smsUserId) {
            uid = smsUserId;
            uname = smsUserName || '';
          } else {
            setError('ログインが必要です');
            setLoading(false);
            return;
          }
        }
        setUserId(uid);
        setUserName(uname);
        const admin = isAdminMode;
        if (admin) {
          setIsAdmin(true);
        }
        await loadData(uid, admin);
      } catch {
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [orderId]);

  const loadData = async (uid?: string, admin?: boolean) => {
    if (!orderId) return;
    const data = await getOrderComments(parseInt(orderId), uid, admin);
    setOrder(data.order);
    setComments(data.comments);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingImage(file);
      setPendingImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('image_type', 'order_comment');
    const response = await fetch(`${AISHA_API_BASE}/menu-images/upload/`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (data.public_url) return data.public_url;
    throw new Error(data.error || '画像のアップロードに失敗しました');
  };

  const handleSendComment = async () => {
    if (!orderId || (!newComment.trim() && !pendingImage)) return;
    setSending(true);
    setUploading(!!pendingImage);
    setError('');
    try {
      let imageUrl = '';
      if (pendingImage) {
        imageUrl = await uploadImage(pendingImage);
      }
      const result = await createOrderComment(parseInt(orderId), {
        user_id: userId,
        user_name: userName,
        is_admin: isAdmin,
        content: newComment.trim(),
        image_url: imageUrl || undefined,
      });
      setComments((prev) => [...prev, result.comment]);
      setOrder(result.order);
      setNewComment('');
      setPendingImage(null);
      setPendingImagePreview('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!orderId) return;
    try {
      const updated = await confirmOrder(parseInt(orderId));
      setOrder(updated);
      setShowConfirmDialog(false);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleCancel = async () => {
    if (!orderId || !confirm('本当にキャンセルしますか？')) return;
    try {
      const updated = await cancelOrder(parseInt(orderId));
      setOrder(updated);
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 text-gray-400 flex items-center justify-center">読み込み中...</div>;
  if (error && !order) return <div className="min-h-screen bg-gray-950 text-red-400 flex items-center justify-center">{error}</div>;
  if (!order) return null;

  const isClosed = order.status === 'cancelled' || order.status === 'reviewed';

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="font-bold text-lg">注文 #{order.id}</span>
          {!isAdminMode && (
            <button onClick={() => navigate('/aineko/orders')} className="text-sm text-gray-400 hover:text-white">
              ← 注文一覧
            </button>
          )}
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-2">
          <OrderStatusProgress status={order.status} compact withInstallation={order.with_installation} />
        </div>
      </header>

      {/* Order summary */}
      <div className="max-w-3xl mx-auto w-full px-4 py-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="flex items-start gap-4">
            {order.image_url && (
              <img
                src={order.image_url}
                alt=""
                className="w-28 h-28 object-cover rounded-lg border border-gray-600 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setExpandedImage(order.image_url)}
              />
            )}
            <div className="flex-1 text-sm text-gray-300 space-y-1">
              <p className="font-medium text-white">{order.product_name}{order.variation_name ? ` / ${order.variation_name}` : ''}</p>
              <p>¥{order.total_amount.toLocaleString()}</p>
              <p className="text-gray-500 text-xs">{new Date(order.created_at).toLocaleString('ja-JP')}</p>
              {order.estimated_delivery_date && (
                <p className="text-orange-400 text-xs">納期: {order.estimated_delivery_date}</p>
              )}
              {order.tracking_number && !order.with_installation && (
                <p className="text-green-400 text-xs">
                  {order.shipping_carrier || '配送'}: {order.tracking_number}
                </p>
              )}
              {order.with_installation && order.shipping_carrier && (
                <div className="text-green-400 text-xs space-y-0.5 mt-1">
                  <p>取付店: {order.shipping_carrier}</p>
                  {order.shipping_carrier === 'アルティザン' && (
                    <p className="text-gray-400">〒213-0014 神奈川県川崎市高津区新作3-25-13 / 050-5865-9672</p>
                  )}
                  {order.tracking_number && <p>取付予定: {order.tracking_number}</p>}
                </div>
              )}
            </div>
            {/* アクションボタン */}
            {!isClosed && (
              <div className="flex flex-col gap-2 flex-shrink-0">
                {/* 確定前: 注文確定ボタン */}
                {(order.status === 'new' || order.status === 'confirming') && (
                  <button
                    onClick={() => setShowConfirmDialog(true)}
                    className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
                  >
                    注文確定
                  </button>
                )}
                {/* 発送済: 到着・評価ボタン（ユーザー側のみ） */}
                {!isAdminMode && order.status === 'shipped' && (
                  <button
                    onClick={() => navigate(`/aineko/order/${order.id}/review`)}
                    className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {order.with_installation ? '取付・評価' : '到着・評価'}
                  </button>
                )}
                {/* キャンセル: 管理者のみ */}
                {isAdmin && (order.status === 'new' || order.status === 'confirming') && (
                  <button
                    onClick={handleCancel}
                    className="bg-red-600/80 hover:bg-red-700 text-white text-xs px-4 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                  >
                    キャンセル
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 overflow-y-auto">
        <div className="space-y-3 py-4">
          {comments.length === 0 && (
            <div className="text-center text-gray-500 py-8">まだコメントはありません</div>
          )}
          {comments.map((c) => (
            <div key={c.id} className={`flex ${c.user_id === userId && !c.is_admin ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                c.is_admin
                  ? 'bg-indigo-900/50 border border-indigo-700'
                  : c.user_id === userId
                    ? 'bg-orange-900/50 border border-orange-700'
                    : 'bg-gray-800 border border-gray-700'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium ${c.is_admin ? 'text-indigo-400' : 'text-orange-400'}`}>
                    {c.is_admin ? '管理者' : c.user_name || 'お客様'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(c.created_at).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                {c.content && <p className="text-sm text-gray-200 whitespace-pre-wrap">{c.content}</p>}
                {c.image_url && (
                  <img
                    src={c.image_url}
                    alt=""
                    className="mt-1.5 max-w-[240px] max-h-[200px] object-contain rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setExpandedImage(c.image_url)}
                  />
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>


      {/* Confirm dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-sm w-full space-y-4 border border-gray-700">
            <h3 className="text-lg font-bold">注文を確定しますか？</h3>
            <p className="text-sm text-gray-400">確定すると製造を開始します。</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowConfirmDialog(false)} className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm">
                戻る
              </button>
              <button onClick={handleConfirm} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm">
                確定する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment input */}
      {!isClosed && (
        <div className="sticky bottom-0 max-w-3xl mx-auto w-full px-4 py-3 border-t border-gray-800 bg-gray-900/80 backdrop-blur-sm">
          {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
          {/* 画像プレビュー */}
          {pendingImagePreview && (
            <div className="mb-2 relative inline-block">
              {uploading && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}
              <img src={pendingImagePreview} alt="" className="max-h-24 rounded-lg border border-gray-600" />
              <button
                onClick={() => { setPendingImage(null); setPendingImagePreview(''); }}
                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-500"
              >
                ✕
              </button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <button
              onClick={() => imageInputRef.current?.click()}
              className="p-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-400 hover:text-white hover:border-gray-500 transition-colors flex-shrink-0"
              title="画像を添付"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendComment()}
              placeholder="メッセージを入力..."
              className="flex-1 p-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-base placeholder-gray-500"
            />
            {uploading ? (
              <div className="bg-orange-600/50 text-white px-5 py-3 rounded-xl flex-shrink-0 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : (
              <button
                onClick={handleSendComment}
                disabled={sending || (!newComment.trim() && !pendingImage)}
                className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-5 py-3 rounded-xl text-sm font-medium transition-colors flex-shrink-0"
              >
                {sending ? '...' : '送信'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* 画像拡大モーダル */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="拡大画像"
            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setExpandedImage(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderConfirmationBoardView;
